import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiScissors, FiStar, FiUsers, FiHeart, FiClock, FiCheck } = FiIcons;

const Services = () => {
  const services = [
    {
      name: 'Classic Haircut',
      description: 'Professional haircut with styling and finishing',
      icon: FiScissors,
      features: ['Consultation', 'Precision Cut', 'Styling', 'Finishing Touch']
    },
    {
      name: 'Beard Trimming & Shaping',
      description: 'Expert beard trimming and precise shaping',
      icon: FiScissors,
      features: ['Beard Analysis', 'Precise Trimming', 'Shape Design', 'Moisturizing']
    },
    {
      name: 'Hair Wash & Treatment',
      description: 'Deep cleansing and nourishing hair treatment',
      icon: FiStar,
      features: ['Deep Cleansing', 'Scalp Massage', 'Conditioning', 'Blow Dry']
    },
    {
      name: 'Hot Towel Service',
      description: 'Relaxing hot towel treatment for face and neck',
      icon: FiHeart,
      features: ['Face Cleansing', 'Hot Towel', 'Moisturizing', 'Relaxation']
    },
    {
      name: 'Traditional Shaving',
      description: 'Classic straight razor shaving experience',
      icon: FiUsers,
      features: ['Pre-shave Oil', 'Hot Lather', 'Precision Shave', 'After-shave Care']
    },
    {
      name: 'Face Treatment',
      description: 'Complete facial grooming and skin care',
      icon: FiStar,
      features: ['Deep Cleansing', 'Exfoliation', 'Face Mask', 'Moisturizing']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-[#826046] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl max-w-3xl mx-auto">
              Experience premium grooming services with our skilled barbers using the finest techniques and products
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8"
              >
                <div className="bg-[#826046] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.name}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-[#826046] mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Mr. Shaver?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We are committed to providing the highest quality grooming services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Expert Barbers', desc: 'Skilled professionals with years of experience', icon: FiUsers },
              { title: 'Premium Products', desc: 'High-quality grooming products and tools', icon: FiStar },
              { title: 'Personalized Service', desc: 'Tailored services to match your style', icon: FiHeart },
              { title: 'Convenient Hours', desc: 'Open daily from 9 AM to 11 PM', icon: FiClock }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-[#826046] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={item.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#826046] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Book Your Appointment Today
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Experience the best grooming services in Dubai. Call us now to schedule your visit.
            </p>
            <a
              href="tel:+971501656278"
              className="bg-yellow-500 text-black px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-300 inline-flex items-center"
            >
              <SafeIcon icon={FiScissors} className="mr-2 w-5 h-5" />
              Call: +971 50 165 6278
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;